/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { ROUTES } from '@sportninja/common/constants/app'
import { useCountries } from '@sportninja/common/hooks/useCountries'
import { useRegistrationData } from '@sportninja/common/hooks/useRegistrationData'
import { getSports } from '@sportninja/common/selectors/types'
import { Empty, Tabs, Button } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import RegistrationDetailsBilling from './RegistrationDetailsBilling'
import RegistrationDetailsInfo from './RegistrationDetailsInfo'

// Dynamic form imports
import { RegistrationDetailsModalProvider } from '../Schedule/RegistrationDetailsModal/context'
import RegistrationInfoSection from '../Schedule/RegistrationDetailsModal/components/RegistrationInfoSection'
import RegistrationDetailsDynamicStandalone from './RegistrationDetailsDynamicStandalone'
import EntityProfileLink from './EntityProfileLink'
import RegistrationHeader from '../Schedule/RegistrationDetailsModal/components/RegistrationHeader'
import useUsersMeRegistration from '@sportninja/common/hooks/useUsersMeRegistration'
import { ArrowLeftOutlined } from '@ant-design/icons'
import Text from 'src/components/ts-components/Text'
import colors from '@sportninja/common/constants/appColors'
import LoadingWrapper from 'src/components/ts-components/LoadingWrapper'

/**
 * 🔄 InfoTabDynamic: Dynamic form wrapper component for registration details
 *
 * This component wraps the dynamic form components in the modal provider context
 * and provides the same functionality as the modal but in a page format.
 */
const ChevronLeftIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='16'
    height='16'
    viewBox='0 0 16 16'
    fill='none'
  >
    <path
      d='M11.3679 1.92333C11.0413 1.59666 10.5146 1.59666 10.1879 1.92333L4.58126 7.53C4.32126 7.79 4.32126 8.21 4.58126 8.47L10.1879 14.0767C10.5146 14.4033 11.0413 14.4033 11.3679 14.0767C11.6946 13.75 11.6946 13.2233 11.3679 12.8967L6.47459 8.00333L11.3746 3.10333C11.6946 2.77666 11.6946 2.25 11.3679 1.92333Z'
      fill='#C1C2C3'
    />
  </svg>
)

const InfoTabDynamic = ({
  scheduleId,
  registrationId,
  countries,
  types,
  registrationDetails,
  registrationLoading,
  registrationError,
}) => {
  return (
    <RegistrationDetailsModalProvider
      scheduleId={scheduleId}
      registrationId={registrationId || undefined}
      countries={countries}
      types={types}
    >
      <InfoTabDynamicContent
        scheduleId={scheduleId}
        registrationId={registrationId}
        registrationDetails={registrationDetails}
        registrationLoading={registrationLoading}
        registrationError={registrationError}
      />
    </RegistrationDetailsModalProvider>
  )
}

/**
 * InfoTabDynamicContent: Inner content component that uses the modal context.
 * Renders the same structure as the modal "Details" tab but without the modal wrapper.
 */
const InfoTabDynamicContent = ({
  scheduleId,
  registrationId,
  registrationDetails,
  registrationLoading,
  registrationError,
}) => {
  const isTeamRegistration = useMemo(
    () => registrationDetails?.registration_type === 'Team',
    [registrationDetails]
  )

  const player = registrationDetails?.player
  const team = registrationDetails?.team

  const playerName =
    player?.full_name ||
    [player?.name_first, player?.name_last].filter(Boolean).join(' ') ||
    ''

  return (
    <LoadingWrapper loading={registrationLoading || !registrationDetails}>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          padding-bottom: 80px;
        `}
      >
        {isTeamRegistration
          ? team?.uid && (
              <EntityProfileLink
                kind='team'
                id={team.uid}
                name={team.name}
                image={team.image}
                disableLink={team.is_bucket}
              />
            )
          : player?.id && (
              <EntityProfileLink
                kind='player'
                id={player.id}
                name={playerName}
                image={player.image}
              />
            )}
        <RegistrationInfoSection
          registrationDetails={registrationDetails as any}
        />
        <RegistrationDetailsDynamicStandalone
          scheduleId={scheduleId}
          registrationUid={registrationId || ''}
          registrationDetails={registrationDetails || null}
          loading={registrationLoading}
          error={registrationError}
        />
      </div>
    </LoadingWrapper>
  )
}

const RegistrationDetails = ({ match }) => {
  const SCHEDULE_ID = match.params.id
  const REGISTRATION_ID = new URLSearchParams(window.location.search).get(
    'registration_id'
  )
  const history = useHistory()
  const location = useLocation()
  const { types, fetchTypes } = useRegistrationData()
  const {
    data: registrationDetails,
    loading: registrationLoading,
    error: registrationError,
    refetch,
  } = useUsersMeRegistration(REGISTRATION_ID || undefined)

  // Local tab state, initialize from URL (/info or /billing) or query (?tab=)
  const initialTab = useMemo(() => {
    const searchParams = new URLSearchParams(location.search)
    const queryTab = (searchParams.get('tab') || '').toLowerCase()
    if (queryTab === 'billing') {
      return 'billing' as const
    }
    if (queryTab === 'info') {
      return 'info' as const
    }

    const path = location.pathname || ''
    if (path.endsWith('/billing')) {
      return 'billing' as const
    }
    if (path.endsWith('/info')) {
      return 'info' as const
    }
    return 'info' as const
  }, [location.pathname, location.search])

  const [activeTab, setActiveTab] = useState<'info' | 'billing'>(initialTab)

  // Version-based form selection: Version 1 = legacy fixed forms, others = dynamic JSON-driven forms
  const registrationVersion = useMemo(() => {
    return registrationDetails?.schedule?.registration_version
  }, [registrationDetails?.schedule?.registration_version])

  const isLegacyVersion = registrationVersion === 1
  const useDynamicForms = !isLegacyVersion

  // No header actions needed - forms handle saves internally
  const actions = []

  // Once we open the schedule details page, we need to refetch the registration details
  useEffect(() => {
    if (REGISTRATION_ID && !registrationDetails) {
      refetch()
    }
  }, [REGISTRATION_ID, refetch, registrationDetails])

  const { countries, refetch: refetchCountries } = useCountries()
  useEffect(() => {
    if (!types || types === null) {
      fetchTypes()
    }
  }, [fetchTypes, types])

  useEffect(() => {
    if (!countries || countries === null || countries?.length === 0) {
      refetchCountries()
    }
  }, [countries, refetchCountries])

  // Tab pages with local keys
  const pages = useMemo(
    () => [
      {
        key: 'info',
        label: 'Registration Info',
        component: useDynamicForms ? (
          <InfoTabDynamic
            scheduleId={SCHEDULE_ID}
            registrationId={REGISTRATION_ID}
            countries={countries}
            types={types}
            registrationDetails={registrationDetails}
            registrationLoading={registrationLoading}
            registrationError={registrationError}
          />
        ) : (
          <RegistrationDetailsInfo
            registrationDetails={(registrationDetails as any) || null}
            countries={countries}
            refetch={refetch}
          />
        ),
        actions: actions,
      },
      {
        key: 'billing',
        label: 'Billing Schedule',
        component: (
          <RegistrationDetailsBilling
            selectedRegistration={(registrationDetails as any) || null}
            currencies={types?.currency || []}
          />
        ),
        actions: [], // No actions for billing tab
      },
    ],
    [
      SCHEDULE_ID,
      useDynamicForms,
      REGISTRATION_ID,
      countries,
      types,
      refetch,
      registrationDetails,
      registrationLoading,
      registrationError,
      actions,
    ]
  )

  // Build tab items for Ant Design Tabs
  const tabItems = pages.map(({ key, label, component }) => ({
    key,
    label,
    children: component,
  }))

  // Local tab change handler
  const handleTabChange = (key: string) => {
    const nextTab = key as 'info' | 'billing'
    setActiveTab(nextTab)

    // Keep URL in sync for deep-linking and refresh behavior
    const search = location.search || ''
    const base = `${ROUTES.REGISTRATION_DETAILS.ROOT}/${SCHEDULE_ID}`
    const nextPath = `${base}/${nextTab}${search}`
    // Avoid duplicate pushes if already matching
    if (location.pathname + location.search !== nextPath) {
      history.replace(nextPath)
    }
  }

  // If the URL changes externally (e.g., direct link), update the active tab
  useEffect(() => {
    if (initialTab !== activeTab) {
      setActiveTab(initialTab)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTab])

  // Refresh registration details whenever switching tabs to keep data fresh, silently to avoid flicker
  useEffect(() => {
    if (REGISTRATION_ID) {
      // Silent refetch keeps existing content rendered (no loading flash)
      refetch(true)
    }
  }, [activeTab, REGISTRATION_ID, refetch])

  // Add a container style for centering and max width
  const pageContainerStyle = css`
    max-width: min(90vw, 1280px);
    margin: 0 auto;
    margin-top: 40px;
    width: 100%;
    .ant-tabs .ant-tabs-tab.ant-tabs-tab-active {
      background-color: transparent !important;
    }
    .ant-tabs-tab {
      background-color: transparent !important;
    }
    .ant-tabs-nav,
    .ant-tabs-nav-wrap {
      background-color: transparent !important;
    }
  `

  const backButtonStyle = css`
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    font-size: 0.9rem;
  `

  const handleBack = () => {
    history.push(ROUTES.REGISTRATIONS.ROOT)
  }

  if (registrationError) {
    return (
      <div css={pageContainerStyle}>
        <div css={backButtonStyle}>
          <Button
            type='link'
            icon={<ArrowLeftOutlined />}
            size='small'
            onClick={handleBack}
          >
            Back
          </Button>
        </div>
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
          `}
        >
          <Empty
            description={registrationError?.message || ''}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      </div>
    )
  }

  return (
    <div css={pageContainerStyle}>
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 4px;
          cursor: pointer;
          margin-bottom: 8px;
        `}
        onClick={handleBack}
      >
        <ChevronLeftIcon />
        <Text
          variant='captionSmall'
          color={colors.NEUTRAL_50}
          weight='semiBold'
          style='text-transform: uppercase; letter-spacing: 1.44px;'
        >
          Back
        </Text>
      </div>
      <div
        css={css`
          margin-bottom: 16px;
        `}
      >
        <RegistrationHeader
          scheduleId={registrationDetails?.schedule?.uid}
          scheduleName={registrationDetails?.schedule?.name}
          scheduleImage={registrationDetails?.schedule?.image}
          isTeamRegistration={registrationDetails?.registration_type === 'Team'}
        />
      </div>
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
        destroyInactiveTabPane={false}
        css={css`
          &.ant-tabs,
          .ant-tabs-nav,
          .ant-tabs-nav-wrap {
            background-color: transparent !important;
          }
        `}
      />
    </div>
  )
}

const mapStateToProps = (state) => {
  return {
    sports: getSports(state),
  }
}

const mapDispatchToProps = (_) => {
  return {}
}
export default connect(mapStateToProps, mapDispatchToProps)(RegistrationDetails)
